import type { FormDataConvertible } from "@inertiajs/core"
import { Head, router } from "@inertiajs/react"
import { useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import SubjectForm from "./_form"
import type { SubjectFormData, SubjectTypeOption } from "./_form"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Props {
  case_record: CaseRecord
  subject: SubjectFormData
  subject_types: SubjectTypeOption[]
  errors?: Record<string, string[]>
}

export default function New({
  case_record,
  subject,
  subject_types,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Subjects", href: `/cases/${case_record.id}/subjects` },
    { title: "New", href: `/cases/${case_record.id}/subjects/new` },
  ]

  const { data, setData, processing } = useForm<SubjectFormData>({
    subject_type_id: subject.subject_type_id,
    type_description: subject.type_description,
    prefix: subject.prefix,
    first_name: subject.first_name,
    middle_name: subject.middle_name,
    last_name: subject.last_name,
    suffix: subject.suffix,
    address: subject.address,
    address2: subject.address2,
    subdivision: subject.subdivision,
    address_dates: subject.address_dates,
    city: subject.city,
    state: subject.state,
    zip: subject.zip,
    home_phone: subject.home_phone,
    cell_phone: subject.cell_phone,
    work_phone: subject.work_phone,
    email: subject.email,
    drivers_license: subject.drivers_license,
    drivers_license_state: subject.drivers_license_state,
    drivers_license_expires: subject.drivers_license_expires,
    drivers_license_name: subject.drivers_license_name,
    date_of_birth: subject.date_of_birth,
    ssn: subject.ssn,
    sex: subject.sex,
    race: subject.race,
    marital_status: subject.marital_status,
    spouse_name: subject.spouse_name,
    divorce_date: subject.divorce_date,
    dependents: subject.dependents,
    law_firm: subject.law_firm,
    attorney: subject.attorney,
    place_of_employment: subject.place_of_employment,
    poe_address: subject.poe_address,
    poe_city: subject.poe_city,
    poe_state: subject.poe_state,
    poe_zip: subject.poe_zip,
    work_schedule: subject.work_schedule,
    hobbies: subject.hobbies,
    description: subject.description,
    internal_notes: subject.internal_notes,
    ins_date_of_loss: subject.ins_date_of_loss,
    ins_injury: subject.ins_injury,
    ins_limits: subject.ins_limits,
    ins_contact: subject.ins_contact,
    ins_contact_phone: subject.ins_contact_phone,
    ins_rehab: subject.ins_rehab,
    ins_may_we_contact: subject.ins_may_we_contact,
    ins_prev_surveillance: subject.ins_prev_surveillance,
    ins_benefits: subject.ins_benefits,
    ins_working: subject.ins_working,
    ins_appts: subject.ins_appts,
    ins_accident: subject.ins_accident,
    ins_rehab_loc: subject.ins_rehab_loc,
    ins_appt_info: subject.ins_appt_info,
    ins_prev_surveillance_date: subject.ins_prev_surveillance_date,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.post(`/cases/${case_record.id}/subjects`, {
      subject: data as unknown as Record<string, FormDataConvertible>,
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Add Subject - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Add Subject</h1>
        <SubjectForm
          caseId={case_record.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Add Subject"
          subject_types={subject_types}
        />
      </div>
    </AppLayout>
  )
}
